import React, { useCallback, useEffect, useState } from 'react'
import { ClipboardCheckIcon, Loader2Icon } from 'lucide-react'
import {
  fetchExpensesForReview,
  reviewExpense,
  type JobExpense,
  type ExpenseStatus,
} from '../api/expenses'
import { usePermissions } from '../hooks/usePermissions'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  mobile,
} from '../components/mobile'

function expenseStatusClass(status: ExpenseStatus): string {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800'
    case 'rejected':
      return 'bg-red-100 text-red-800'
    case 'reimbursed':
      return 'bg-blue-100 text-blue-800'
    default:
      return 'bg-yellow-100 text-yellow-800'
  }
}

export function ExpenseReview() {
  const { can, loading: permLoading } = usePermissions()
  const canReview = can('expenses.review')
  const [items, setItems] = useState<JobExpense[]>([])
  const [loading, setLoading] = useState(true)
  const [actingId, setActingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await fetchExpensesForReview())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load expenses')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (permLoading || !canReview) return
    load()
  }, [permLoading, canReview, load])

  const handleReview = async (id: string, status: 'approved' | 'rejected' | 'reimbursed') => {
    setActingId(id)
    setError(null)
    try {
      await reviewExpense(id, status)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed')
    } finally {
      setActingId(null)
    }
  }

  if (permLoading) {
    return (
      <MobilePage className="flex justify-center items-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </MobilePage>
    )
  }

  if (!canReview) {
    return (
      <MobilePage>
        <p className="text-gray-600">You do not have permission to review expenses.</p>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Expense review"
        subtitle="Approve, reject, or mark expenses as reimbursed"
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
      )}

      <div className="native-mobile-list space-y-2">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <MobileEmptyState
            icon={<ClipboardCheckIcon className="w-12 h-12" />}
            title="No expenses to review"
          />
        ) : (
          items.map((row) => {
            const busy = actingId === row.id
            return (
              <MobileListRow
                key={row.id}
                title={row.description}
                subtitle={row.job_id ? `Job ${row.job_id.slice(0, 8)}…` : undefined}
                badges={
                  <span className={`${mobile.badge} ${expenseStatusClass(row.status)}`}>
                    {row.status}
                  </span>
                }
                meta={
                  <>
                    <div>
                      {row.paid_out_of_pocket && row.amount != null
                        ? `$${row.amount.toFixed(2)}`
                        : 'No out-of-pocket amount'}
                    </div>
                    <div>Submitted {new Date(row.created_at).toLocaleDateString()}</div>
                  </>
                }
                chevron={false}
                footer={
                  <>
                    <MobileRowAction
                      tone="success"
                      onClick={() => !busy && row.status !== 'approved' && handleReview(row.id, 'approved')}
                      className={busy || row.status === 'approved' ? 'opacity-40 pointer-events-none' : ''}
                    >
                      Approve
                    </MobileRowAction>
                    <MobileRowAction
                      tone="danger"
                      onClick={() => !busy && row.status !== 'rejected' && handleReview(row.id, 'rejected')}
                      className={busy || row.status === 'rejected' ? 'opacity-40 pointer-events-none' : ''}
                    >
                      Reject
                    </MobileRowAction>
                    <MobileRowAction
                      tone="primary"
                      onClick={() => !busy && row.status !== 'reimbursed' && handleReview(row.id, 'reimbursed')}
                      className={busy || row.status === 'reimbursed' ? 'opacity-40 pointer-events-none' : ''}
                    >
                      Reimburse
                    </MobileRowAction>
                  </>
                }
              />
            )
          })
        )}
      </div>

      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 px-6 py-8">No expenses to review.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((row) => {
                  const busy = actingId === row.id
                  return (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-gray-900">{row.description}</td>
                      <td className="px-4 py-4 text-sm text-gray-600 font-mono text-xs">
                        {row.job_id ? row.job_id.slice(0, 8) + '…' : '—'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {row.paid_out_of_pocket && row.amount != null ? `$${row.amount.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${expenseStatusClass(row.status)}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {new Date(row.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap">
                        <button
                          type="button"
                          disabled={busy || row.status === 'approved'}
                          onClick={() => handleReview(row.id, 'approved')}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={busy || row.status === 'rejected'}
                          onClick={() => handleReview(row.id, 'rejected')}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          disabled={busy || row.status === 'reimbursed'}
                          onClick={() => handleReview(row.id, 'reimbursed')}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                        >
                          Mark reimbursed
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MobilePage>
  )
}
